import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Users, Plus, Trash2, Mail, Loader2, Crown, X } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

/**
 * TeamMembers — Käyttäjät-osio Asetuksissa (Release 2A).
 *
 * Ainoastaan pääkäyttäjä (role === "owner" / "admin") näkee ja käyttää tätä
 * osiota. Osio listaa nykyiset tiimin jäsenet + odottavat kutsut ja tarjoaa
 * lomakkeen uuden käyttäjän kutsumiseen sähköpostilla.
 */
export default function TeamMembers() {
  const { user } = useAuth();
  const isOwner = user?.role === "owner" || user?.role === "admin";
  const [members, setMembers] = useState([]);
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [emailInput, setEmailInput] = useState("");
  const [inviting, setInviting] = useState(false);
  const [busyId, setBusyId] = useState("");

  const refresh = async () => {
    try {
      const [m, i] = await Promise.all([
        api.get("/company/members"),
        api.get("/company/invites"),
      ]);
      setMembers(m.data.members || []);
      setInvites(i.data.invites || []);
    } catch (e) {
      // Silent — a member (non-owner) will simply see nothing here.
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (isOwner) refresh(); else setLoading(false); }, [isOwner]);

  if (!isOwner) return null;

  const invite = async (e) => {
    e?.preventDefault();
    const email = emailInput.trim().toLowerCase();
    if (!email || !email.includes("@")) {
      toast.error("Anna kelvollinen sähköpostiosoite");
      return;
    }
    setInviting(true);
    try {
      const { data } = await api.post("/company/invites", { email });
      toast.success(data.resent ? "Kutsu lähetetty uudelleen" : "Kutsu lähetetty sähköpostilla");
      setEmailInput("");
      await refresh();
    } catch (e) {
      const msg = e?.response?.data?.detail || "Kutsun luonti epäonnistui";
      toast.error(typeof msg === "string" ? msg : "Kutsun luonti epäonnistui");
    } finally {
      setInviting(false);
    }
  };

  const revoke = async (inviteId) => {
    setBusyId(inviteId);
    try {
      await api.delete(`/company/invites/${inviteId}`);
      setInvites((prev) => prev.filter((x) => x.invite_id !== inviteId));
      toast.success("Kutsu peruutettu");
    } catch {
      toast.error("Peruutus epäonnistui");
    } finally {
      setBusyId("");
    }
  };

  const removeMember = async (m) => {
    if (m.role === "owner") return;
    if (!confirm(`Poista ${m.name || m.email} tiimistä?`)) return;
    setBusyId(m.user_id);
    try {
      await api.delete(`/company/members/${m.user_id}`);
      setMembers((prev) => prev.filter((x) => x.user_id !== m.user_id));
      toast.success("Käyttäjä poistettu tiimistä");
    } catch {
      toast.error("Poisto epäonnistui");
    } finally {
      setBusyId("");
    }
  };

  return (
    <div className="card-soft p-5 md:p-8" data-testid="team-members-card">
      <div className="flex items-start gap-3 mb-5">
        <div className="h-10 w-10 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center ring-1 ring-emerald-100 flex-shrink-0">
          <Users className="h-5 w-5" />
        </div>
        <div className="flex-1">
          <div className="text-xs uppercase tracking-widest text-emerald-600 font-semibold">Tiimi</div>
          <h2 className="font-display text-xl md:text-2xl font-semibold text-slate-900 mt-0.5">Käyttäjät</h2>
          <p className="text-sm text-slate-600 mt-1 leading-relaxed">
            Kutsu kenttätyöntekijöitä käyttämään Verasoa. He näkevät yrityksen työt ja voivat käyttää valmiita työpohjia — mutta eivät hallinnointitoimintoja.
          </p>
        </div>
      </div>

      {/* Invite form */}
      <form onSubmit={invite} className="flex flex-col sm:flex-row items-stretch gap-2 mb-5" data-testid="team-invite-form">
        <div className="flex-1 relative">
          <Mail className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            type="email"
            value={emailInput}
            onChange={(e) => setEmailInput(e.target.value)}
            placeholder="asentaja@yritys.fi"
            className="input-base pl-10 w-full"
            data-testid="team-invite-email-input"
            required
          />
        </div>
        <button
          type="submit"
          disabled={inviting}
          className="inline-flex items-center justify-center gap-2 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-semibold px-5 py-2.5 text-sm disabled:opacity-50 shadow-sm"
          data-testid="team-invite-submit"
        >
          {inviting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
          Lähetä kutsu
        </button>
      </form>

      {loading ? (
        <div className="text-sm text-slate-500 py-4">Ladataan…</div>
      ) : (
        <div className="space-y-3">
          {members.map((m) => (
            <div key={m.user_id} className="flex items-center gap-3 rounded-xl bg-slate-50 border border-slate-100 px-4 py-3" data-testid={`team-member-${m.user_id}`}>
              <div className="h-9 w-9 rounded-full bg-white border border-slate-200 flex items-center justify-center text-sm font-bold text-slate-700 flex-shrink-0">
                {(m.name || m.email || "?").slice(0, 1).toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <div className="text-sm font-semibold text-slate-900 truncate">{m.name || m.email}</div>
                  {m.role === "owner" && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 text-[10px] font-semibold ring-1 ring-amber-100">
                      <Crown className="h-2.5 w-2.5" /> Pääkäyttäjä
                    </span>
                  )}
                </div>
                <div className="text-xs text-slate-500 truncate">{m.email}</div>
              </div>
              {m.role !== "owner" && (
                <button
                  onClick={() => removeMember(m)}
                  disabled={busyId === m.user_id}
                  className="p-2 rounded-lg text-slate-400 hover:text-red-600 hover:bg-white disabled:opacity-40"
                  data-testid={`team-member-remove-${m.user_id}`}
                  aria-label="Poista tiimistä"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              )}
            </div>
          ))}

          {invites.length > 0 && (
            <div className="pt-2">
              <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold mb-2">Odottavat kutsut</div>
              {invites.map((inv) => (
                <div key={inv.invite_id} className="flex items-center gap-3 rounded-xl bg-white border border-dashed border-slate-300 px-4 py-3 mb-2" data-testid={`team-invite-${inv.invite_id}`}>
                  <div className="h-9 w-9 rounded-full bg-slate-50 flex items-center justify-center text-slate-400 flex-shrink-0">
                    <Mail className="h-4 w-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-slate-900 truncate">{inv.email}</div>
                    <div className="text-xs text-slate-500">Odottaa vastausta</div>
                  </div>
                  <button
                    onClick={() => revoke(inv.invite_id)}
                    disabled={busyId === inv.invite_id}
                    className="p-2 rounded-lg text-slate-400 hover:text-red-600 hover:bg-slate-50 disabled:opacity-40"
                    data-testid={`team-invite-revoke-${inv.invite_id}`}
                    aria-label="Peruuta kutsu"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
